import { encerrarSessao } from "./sessao";
import { useCallback, useEffect, useState } from "react";
import { TABS } from "./config";
import { useFaixaDeAbas } from "./faixa-abas";
import type { Refs, Row } from "./types";
import { api, setSessionUser } from "./api";
import { ICONS, IconCalendar, IconSun, IconMoon, IconLogout, IconHome } from "./icons";
import AnimatedBackground from "./components/AnimatedBackground";
import Login from "./components/Login";
import logoBranca from "./assets/logo-branca.png";
import { applyTheme, getInitialTheme, type Theme } from "./theme";
import AgendaView from "./components/AgendaView";
import InspecaoVeiculoView from "./components/InspecaoVeiculoView";
import SolicitarFolgaView from "./components/SolicitarFolgaView";
import DocumentosVeiculosView from "./components/DocumentosVeiculosView";
import TrafosReformaView from "./components/TrafosReformaView";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import { AvisoReuniao } from "./components/PedirReuniaoView";
import AccessGate from "./components/AccessGate";
import AvisoSistema from "./components/AvisoSistema";
import { PermissoesProvider } from "./permissoes";
import { ACESSO_COMPLETO, ativarModoLeitura, podeVerAba, type Visualizacao } from "./modo-leitura";
import {
  readSsoUser,
  clearSsoFromUrl,
  goToSystem,
  SYSTEMS,
  checkAcesso,
  isAdminSupremo,
} from "./integration";

/** Estado do portão de acesso (Fase 2 — bloqueio por sistema). */
type GateStatus = "checking" | "allowed" | "denied" | "error";

const SESSION_KEY = "sercamp-colaborador-user";

/** Login inicial: prioriza o SSO vindo do Portal/outros sistemas (URL) e cai na sessão salva. */
function getInitialUser(): string | null {
  const sso = readSsoUser();
  if (sso) {
    localStorage.setItem(SESSION_KEY, sso);
    return sso;
  }
  return localStorage.getItem(SESSION_KEY);
}

/** Aba inicial via deep-link do Portal (#tab=chave); cai em "agenda" se inválida. */
const INITIAL_TAB: string = (() => {
  try {
    const m = window.location.hash.match(/[#&]tab=([^&]+)/);
    const k = m ? decodeURIComponent(m[1]) : "";
    return k && (k === "agenda" || TABS.some((t) => t.key === k)) ? k : "agenda";
  } catch {
    return "agenda";
  }
})();

export default function App() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [user, setUser] = useState<string | null>(getInitialUser);

  useEffect(() => {
    clearSsoFromUrl();
  }, []);
  const [active, setActive] = useState<string>(INITIAL_TAB);
  const [refs, setRefs] = useState<Refs>({ colaboradores: [], obras: [], equipamentos: [] });
  const [isAdmin, setIsAdmin] = useState(false);
  // Só admin do Portal exclui registros — desce para a árvore e esconde os botões.
  const [podeExcluir, setPodeExcluir] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // Colaborador vinculado ao login — usado para "Minha Agenda" (só a escala dele).
  const [colaboradorId, setColaboradorId] = useState<number | null>(null);
  // Permissões de aba do usuário: null = sem restrição (admin/legado -> vê tudo).
  const [permissoes, setPermissoes] = useState<string[] | null>(null);
  // Enquanto o /api/me não resolve, não renderiza o app (evita "flash" de abas).
  const [permsLoaded, setPermsLoaded] = useState(false);
  // Portão de acesso: enquanto não confirmado, não renderiza o app.
  const [gate, setGate] = useState<GateStatus>(user ? "checking" : "allowed");
  // Somente visualização (cadastro do Portal): abas liberadas e nada de gravar.
  const [vis, setVis] = useState<Visualizacao>(ACESSO_COMPLETO);
  /* No celular o menu vira uma faixa que rola de lado: isto mantem a aba
     aberta a vista (ver src/faixa-abas.tsx). */
  const navRef = useFaixaDeAbas(active, gate === "allowed");

  // Fase 2 — portão de acesso: sempre que houver usuário atual (SSO novo ou sessão
  // restaurada), confirma com o Portal se ele pode usar ESTE sistema antes de liberar.
  useEffect(() => {
    if (!user) {
      setGate("allowed");
      return;
    }
    // Admin Supremo: sempre liberado (não depende da rede para o portão).
    if (isAdminSupremo(user)) {
      setGate("allowed");
      return;
    }
    let cancelled = false;
    setGate("checking");
    checkAcesso(user)
      .then((r) => {
        if (cancelled) return;
        setVis({ somenteLeitura: r.somenteLeitura, abas: r.abas });
        setGate(r.allowed ? "allowed" : "denied");
      })
      .catch(() => {
        if (!cancelled) setGate("error");
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Resolve dados do usuário logado (admin? nome? colaborador vinculado? permissões?).
  useEffect(() => {
    setSessionUser(user ?? "");
    if (!user) {
      setIsAdmin(false);
      setPodeExcluir(false);
      setDisplayName(null);
      setColaboradorId(null);
      setPermissoes(null);
      setPermsLoaded(false);
      return;
    }
    setPermsLoaded(false);
    api
      .me(user)
      .then((r) => {
        setIsAdmin(!!r.isAdmin);
        setPodeExcluir(!!r.podeExcluir);
        setDisplayName(r.nome || null);
        setColaboradorId(r.colaborador_id ?? null);
        setPermissoes(r.isAdmin ? null : r.permissoes ?? null);
        setPermsLoaded(true);
      })
      .catch(() => {
        // Fail-closed: se não deu para confirmar as permissões, não libera nada.
        setIsAdmin(false);
        setPodeExcluir(false);
        setDisplayName(null);
        setColaboradorId(null);
        setPermissoes([]);
        setPermsLoaded(true);
      });
  }, [user]);

  // Pode ver esta aba? Admin e usuário sem restrição (permissoes=null) veem tudo.
  const canView = useCallback(
    (key: string): boolean => {
      /* "Solicitar Atualização" é de TODO MUNDO: é o colaborador que usa a tela
         no campo quem percebe o que precisa mudar. Não depende de permissão. */
      if (key === "solicitar_atualizacao") return true;
      /* "Documentos dos Veículos" também: é consulta (só ver e baixar) e o
         pedido foi que todo usuário do Operacional tenha a aba. */
      if (key === "documentos_veiculos") return true;
      /* "Trafos em Reforma" também: o pedido foi que CADA usuário acompanhe a
         entrada do trafo e as etapas seguintes. */
      if (key === "trafos_reforma") return true;
      if (isAdmin || permissoes === null) return true;
      return permissoes.includes(key);
    },
    [isAdmin, permissoes],
  );

  /*
   * Somente visualização (cadastro do Portal): além das permissões deste app,
   * só as abas que o Portal liberou — as duas regras valem juntas (E).
   * "Solicitar Atualização" é um pedido, não consulta: some para quem só visualiza.
   */
  const verAba = useCallback(
    (key: string): boolean =>
      canView(key) &&
      (podeVerAba(vis, key) || key === "documentos_veiculos") &&
      !(vis.somenteLeitura && key === "solicitar_atualizacao"),
    [canView, vis],
  );

  // Somente visualização: faixa de aviso, sem botões de gravar e sem gravação.
  useEffect(() => {
    ativarModoLeitura(gate === "allowed" && vis.somenteLeitura);
  }, [gate, vis.somenteLeitura]);

  // Se a aba ativa não é permitida, redireciona para a primeira aba liberada.
  useEffect(() => {
    if (verAba(active)) return;
    const ordem = ["agenda", ...TABS.map((t) => t.key)];
    const primeira = ordem.find((k) => verAba(k));
    setActive(primeira ?? "agenda");
  }, [active, verAba]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  // Carrega os cadastros de apoio (colaboradores) usados para exibir nomes na agenda.
  const loadRefs = useCallback(async () => {
    try {
      const colaboradores = (await api.list("colaboradores")) as Row[];
      setRefs({ colaboradores, obras: [], equipamentos: [] });
    } catch {
      /* sem permissão de listar cadastros — a agenda ainda funciona pelo id */
    }
  }, []);

  useEffect(() => {
    if (user) loadRefs();
  }, [user, loadRefs]);

  function login(name: string) {
    localStorage.setItem(SESSION_KEY, name);
    setUser(name);
  }

  function logout() {
    localStorage.removeItem(SESSION_KEY);
    encerrarSessao();
    setUser(null);
    setVis(ACESSO_COMPLETO);
  }

  if (!user) {
    return <Login onLogin={login} theme={theme} onToggleTheme={toggleTheme} />;
  }

  // Portão de acesso (Fase 2): só libera o app quando o Portal confirma o acesso.
  if (gate !== "allowed") {
    if (gate === "denied") localStorage.removeItem(SESSION_KEY);
    return (
      <AccessGate
        status={gate === "checking" ? "checking" : gate === "denied" ? "denied" : "error"}
        user={user}
        theme={theme}
      />
    );
  }

  // Aguarda as permissões (/api/me) antes de montar o menu — sem flash de abas.
  if (!permsLoaded) {
    return <AccessGate status="checking" user={user} theme={theme} />;
  }

  const tab = TABS.find((t) => t.key === active);
  const title = active === "agenda" ? "Minha Agenda" : tab?.label ?? "";
  const subtitle =
    active === "agenda"
      ? "Sua escala — os serviços em que você foi escalado"
      : tab?.subtitle ?? "";

  return (
    <PermissoesProvider value={podeExcluir}>
      <AvisoSistema />
      <AvisoReuniao usuario={user} onAbrir={() => setActive("solicitar_atualizacao")} />
      <div className="app">
        <AnimatedBackground />

        <aside className="sidebar">
          <div className="brand">
            <img src={logoBranca} alt="SERCAMP" className="brand-logo-img" />
          </div>

          <nav className="nav" ref={navRef}>
            {verAba("agenda") && (
              <button
                className={`nav-item ${active === "agenda" ? "active" : ""}`}
                onClick={() => setActive("agenda")}
              >
                <span className="nav-icon">
                  <IconCalendar size={19} />
                </span>
                Minha Agenda
              </button>
            )}
            {TABS.filter((t) => verAba(t.key)).map((t) => {
              const Icon = ICONS[t.icon];
              return (
                <button
                  key={t.key}
                  className={`nav-item ${active === t.key ? "active" : ""}`}
                  onClick={() => setActive(t.key)}
                >
                  <span className="nav-icon">
                    <Icon size={19} />
                  </span>
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="content">
          <header className="topbar">
            <div>
              <h1>{title}</h1>
              <p className="muted">{subtitle}</p>
            </div>

            <div className="topbar-actions">
              <button
                className="theme-toggle"
                onClick={() => goToSystem(SYSTEMS.portal, user)}
                title="Portal SERCAMP"
              >
                <IconHome size={18} />
              </button>
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              >
                {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
              <div className="user-chip">
                <span className="user-avatar">{user.charAt(0).toUpperCase()}</span>
                <span className="user-name">{displayName || user}</span>
              </div>
              <button className="theme-toggle" onClick={logout} title="Sair">
                <IconLogout size={18} />
              </button>
            </div>
          </header>

          <section className="view">
            {!verAba(active) ? (
              <div className="empty">Selecione uma opção no menu.</div>
            ) : active === "agenda" ? (
              <AgendaView refs={refs} colaboradorId={colaboradorId} />
            ) : active === "trafos_reforma" ? (
              // Somente visualização: lista e fichas abertas para consulta, sem gravar.
              <TrafosReformaView somenteLeitura={vis.somenteLeitura} />
            ) : active === "inspecao_veiculo" ? (
              // Somente visualização: o checklist aparece travado (sem foto,
              // sem digitar) e o histórico continua consultável.
              <InspecaoVeiculoView somenteLeitura={vis.somenteLeitura} />
            ) : active === "documentos_veiculos" ? (
              <DocumentosVeiculosView />
            ) : active === "solicitar_folga" ? (
              <SolicitarFolgaView somenteLeitura={vis.somenteLeitura} />
            ) : active === "solicitar_atualizacao" ? (
              // A aba que existe em todos os sistemas: o pedido de ajuste vai
              // para o sistema de Desenvolvimento (server/solicitar-atualizacao.ts).
              <SolicitarAtualizacaoView usuario={user} />
            ) : null}
          </section>
        </main>
      </div>
    </PermissoesProvider>
  );
}
